import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Row, Col, Table } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { Get, Post, Put } from '../../utils/httpProvider';
import { ANNUAL_JOB_API, ANNUAL_JOB_REGISTRATION_API } from '../../utils/apiUrl';
import { toast } from 'react-toastify';
import moment from 'moment';
import DatePicker from '@app/components/CustomDatePicker';
import Select from 'react-select';

const AnnualJobRegistrationForm = () => {
    const { id } = useParams();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [availableJobs, setAvailableJobs] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        startDate: '',
        endDate: '',
        note: '',
        status: 0,
        details: [] // { annualJobId, registeredKPI, jobName, unit, weight, baseQuantity }
    });

    useEffect(() => {
        fetchAvailableJobs();
        if (id && id !== 'new') {
            fetchRegistrationDetail(id);
        }
    }, [id]);

    const fetchAvailableJobs = async () => {
        try {
            const res = await Get(ANNUAL_JOB_API.GET_ALL_ACTIVE);
            if (res) {
                const dataObj = res.data ?? res.Data ?? res;
                const rawJobs = Array.isArray(dataObj) ? dataObj : (dataObj.$values || []);
                const normalized = rawJobs.map(j => ({
                    id: j.Id ?? j.id,
                    name: j.Name ?? j.name,
                    unit: j.Unit ?? j.unit,
                    weight: j.Weight ?? j.weight,
                    baseQuantity: j.BaseQuantity ?? j.baseQuantity
                }));
                setAvailableJobs(normalized);
            }
        } catch (error) {
            console.error('Error fetching jobs:', error);
        }
    };

    const fetchRegistrationDetail = async (regId) => {
        setLoading(true);
        try {
            const res = await Get(`${ANNUAL_JOB_REGISTRATION_API.GET_BY_ID}/${regId}`);
            if (res) {
                const reg = res.data ?? res.Data ?? res;
                const rawDetails = reg.Details ?? reg.details;
                const details = (rawDetails && rawDetails.$values ? rawDetails.$values : rawDetails) || [];
                setFormData({
                    name: reg.Name ?? reg.name ?? '',
                    startDate: (reg.StartDate ?? reg.startDate) ? moment(reg.StartDate ?? reg.startDate).format('YYYY-MM-DD') : '',
                    endDate: (reg.EndDate ?? reg.endDate) ? moment(reg.EndDate ?? reg.endDate).format('YYYY-MM-DD') : '',
                    note: reg.Note ?? reg.note ?? '',
                    status: reg.Status ?? reg.status ?? 0,
                    details: details.map(d => ({
                        annualJobId: d.AnnualJobId ?? d.annualJobId,
                        registeredKPI: d.RegisteredKPI ?? d.registeredKPI,
                        registeredPercent: d.RegisteredPercent ?? d.registeredPercent ?? 100,
                        jobName: d.AnnualJobName ?? d.annualJobName ?? d.JobName ?? d.jobName,
                        unit: d.AnnualJobUnit ?? d.annualJobUnit ?? d.Unit ?? d.unit,
                        weight: d.AnnualJobWeight ?? d.annualJobWeight ?? d.Weight ?? d.weight,
                        baseQuantity: d.AnnualJobBaseQuantity ?? d.annualJobBaseQuantity ?? d.BaseQuantity ?? d.baseQuantity,
                        note: d.Note ?? d.note ?? '',
                        endDate: (d.EndDate ?? d.endDate) ? moment(d.EndDate ?? d.endDate).format('YYYY-MM-DD') : ''
                    }))
                });
            }
        } catch (error) {
            console.error('Error fetching registration:', error);
            toast.error('Không thể tải dữ liệu phiếu');
            history.push('/annual-job/registration');
        } finally {
            setLoading(false);
        }
    };

    const handleAddJob = (selectedOption) => {
        if (!selectedOption) return;
        const jobId = parseInt(selectedOption.value);
        if (!jobId) return;

        // Check if already added
        if (formData.details.find(d => d.annualJobId === jobId)) {
            toast.warning('Công việc này đã được thêm');
            return;
        }

        const job = availableJobs.find(j => j.id === jobId);
        if (job) {
            setFormData({
                ...formData,
                details: [...formData.details, {
                    annualJobId: job.id,
                    registeredKPI: 1,
                    registeredPercent: 100,
                    jobName: job.name,
                    unit: job.unit,
                    weight: job.weight,
                    baseQuantity: job.baseQuantity,
                    note: '',
                    endDate: ''
                }]
            });
        }
    };

    const handleRemoveJob = (jobId) => {
        setFormData({
            ...formData,
            details: formData.details.filter(d => d.annualJobId !== jobId)
        });
    };

    const handleUpdateKPI = (jobId, value) => {
        let kpiStr = value.replace(/^0+(?=\d)/, '');
        if (kpiStr !== '' && isNaN(parseInt(kpiStr, 10))) kpiStr = '';

        setFormData({
            ...formData,
            details: formData.details.map(d => 
                d.annualJobId === jobId ? { ...d, registeredKPI: kpiStr } : d
            )
        });
    };

    const handleUpdatePercent = (jobId, value) => {
        let percentStr = value.replace(/^0+(?=\d)/, '');
        if (percentStr !== '') {
            const num = parseFloat(percentStr);
            if (isNaN(num)) {
                percentStr = '';
            } else if (num < 0) {
                percentStr = '0';
            }
        }
        setFormData({
            ...formData,
            details: formData.details.map(d => 
                d.annualJobId === jobId ? { ...d, registeredPercent: percentStr } : d
            )
        });
    };
// thêm hàm xử lý lấy dữ liệu từ input 
    const handleUpdateJobNote = (jobId, value) => {
        setFormData({
            ...formData,
            details: formData.details.map(d => 
                d.annualJobId === jobId ? { ...d, note: value } : d
            )
        });
    };

    const handleUpdateJobEndDate = (jobId, value) => {
        setFormData({
            ...formData,
            details: formData.details.map(d => 
                d.annualJobId === jobId ? { ...d, endDate: value } : d
            )
        });
    };

    const calculateConvertedScore = (item) => {
        const baseQty = Number(item.baseQuantity) || 1;
        const percent = Number(item.registeredPercent) ?? 100;
        return (Number(item.registeredKPI) || 0) * (Number(item.weight) || 0) / baseQty * (percent / 100);
    };

    const totalConvertedScore = formData.details.reduce(
        (sum, item) => sum + calculateConvertedScore(item),
        0
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.details.length === 0) {
            toast.warning('Vui lòng chọn ít nhất 1 công việc');
            return;
        }

        if (formData.startDate > formData.endDate) {
            toast.warning('Ngày kết thúc phải sau ngày bắt đầu');
            return;
        }

        if (formData.endDate) {
            const overdueDetail = formData.details.find(d => d.endDate && moment(d.endDate).isAfter(moment(formData.endDate), 'day'));
            if (overdueDetail) {
                toast.warning(`Hạn chót của công việc "${overdueDetail.jobName}" không được vượt quá ngày kết thúc của Phiếu đăng ký.`);
                return;
            }
        }

        setLoading(true);
        try {
            const payload = {
                name: formData.name,
                startDate: formData.startDate,
                endDate: formData.endDate,
                note: formData.note,
                details: formData.details.map(d => ({
                    annualJobId: d.annualJobId,
                    registeredKPI: Number(d.registeredKPI) || 0,
                    registeredPercent: Number(d.registeredPercent) || 0,
                    note: d.note,
                    endDate: d.endDate
                }))
            };

            if (id && id !== 'new') {
                await Put(ANNUAL_JOB_REGISTRATION_API.UPDATE, id, payload);
                toast.success('Cập nhật phiếu thành công');
            } else {
                await Post(ANNUAL_JOB_REGISTRATION_API.CREATE, null, payload);
                toast.success('Tạo phiếu đăng ký thành công');
            }
            history.push('/annual-job/registration');
        } catch (error) {
            console.error('Error saving registration:', error);
            toast.error('Có lỗi xảy ra');
        } finally {
            setLoading(false);
        }
    };

    const isReadonly = formData.status === 1 || formData.status === 2; // Chờ duyệt hoặc Đã duyệt thì ko cho sửa

    if (loading && id !== 'new') return <div className="text-center p-5">Đang tải...</div>;

    return (
        <div className="container-fluid">
            <Card>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="card-title">
                            {id === 'new' ? 'Tạo phiếu đăng ký công việc thường niên' : 'Chi tiết phiếu đăng ký'}
                        </h3>
                        <Button variant="secondary" size="sm" onClick={() => history.push('/annual-job/registration')}>
                            <i className="fas fa-arrow-left"></i> Quay lại
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>

                <Form onSubmit={handleSubmit}>
                    <Row className="mb-4">
                        <Col md={12} className="mb-3">
                            <Form.Group>
                                <Form.Label>Tên phiếu đăng ký <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    required
                                    placeholder="VD: Đăng ký KPI năm học 2024 - 2025"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    disabled={isReadonly}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Form.Group>
                                <Form.Label>Ngày bắt đầu <span className="text-danger">*</span></Form.Label>
                                <DatePicker 
                                    className="form-control"
                                    selected={formData.startDate ? new Date(formData.startDate) : null}
                                    onChange={(date) => setFormData({ ...formData, startDate: date ? moment(date).format('YYYY-MM-DD') : '' })}
                                    disabled={isReadonly}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="dd/MM/yyyy"
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Form.Group>
                                <Form.Label>Ngày kết thúc <span className="text-danger">*</span></Form.Label>
                                <DatePicker 
                                    className="form-control"
                                    selected={formData.endDate ? new Date(formData.endDate) : null}
                                    onChange={(date) => setFormData({ ...formData, endDate: date ? moment(date).format('YYYY-MM-DD') : '' })}
                                    disabled={isReadonly}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="dd/MM/yyyy"
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label>Ghi chú</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={formData.note}
                                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                                    disabled={isReadonly}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <h6 className="fw-bold mb-3 text-secondary border-bottom pb-2">Danh sách công việc đăng ký</h6>
                    
                    {!isReadonly && (
                        <div className="mb-3">
                            <Select
                                value={null}
                                onChange={handleAddJob}
                                options={availableJobs.map(job => ({
                                    value: job.id,
                                    label: `${job.name} (Khối lượng chuẩn: ${job.baseQuantity} ${job.unit} = ${job.weight} điểm)`
                                }))}
                                placeholder="-- Chọn công việc --"
                                isSearchable
                            />
                        </div>
                    )}

                    <div className="table-responsive">
                        <Table striped bordered hover className="align-middle">
                            <thead className="bg-light text-center">
                                <tr>
                                    <th style={{ width: '50px' }}>STT</th>
                                    <th>Tên công việc</th>
                                    <th style={{ width: '150px' }}>Ghi chú</th>
                                    <th style={{ width: '150px' }}>Ngày kết thúc</th>
                                    <th style={{ width: '120px' }}>Đơn vị tính</th>
                                    <th style={{ width: '200px' }}>Quy đổi (Chuẩn/Điểm)</th>
                                    <th style={{ width: '150px' }}>Số lượng đăng ký</th>
                                    <th style={{ width: '120px' }}>Tỉ lệ (%)</th>
                                    <th style={{ width: '140px' }}>Điểm quy đổi</th>
                                    {!isReadonly && <th style={{ width: '80px' }}>Thao tác</th>}
                                </tr>
                            </thead>
                        <tbody>
                            {formData.details.length === 0 ? (
                                <tr>
                                    <td colSpan={!isReadonly ? 10 : 9} className="text-center text-muted py-4">Chưa có công việc nào được chọn</td>
                                </tr>
                            ) : (
                                formData.details.map((item, idx) => {
                                    const baseQty = Number(item.baseQuantity) || 1;
                                    const score = calculateConvertedScore(item);
                                    return (
                                        <tr key={item.annualJobId}>
                                            <td className="text-center">{idx + 1}</td>
                                            <td className="font-weight-bold">{item.jobName}</td>
                                            <td>
                                                {isReadonly ? (
                                                    <span>{item.note}</span>
                                                ) : (
                                                    <Form.Control
                                                        type="text"
                                                        value={item.note || ''}
                                                        onChange={(e) => handleUpdateJobNote(item.annualJobId, e.target.value)}
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                {isReadonly ? (
                                                    <span>{item.endDate ? moment(item.endDate).format('DD/MM/YYYY') : ''}</span>
                                                ) : (
                                                    <DatePicker 
                                                        className="form-control"
                                                        selected={item.endDate ? new Date(item.endDate) : null}
                                                        maxDate={formData.endDate ? new Date(formData.endDate) : null}
                                                        onChange={(date) => handleUpdateJobEndDate(item.annualJobId, date ? moment(date).format('YYYY-MM-DD') : '')}
                                                        dateFormat="dd/MM/yyyy"
                                                        placeholderText="dd/MM/yyyy"
                                                        isClearable
                                                    />
                                                )}
                                            </td>
                                            <td className="text-center">{item.unit}</td>
                                            <td className="text-center">{baseQty} {item.unit} = {item.weight} điểm</td>
                                            <td className="text-center">
                                                {isReadonly ? (
                                                    <span className="fw-bold text-primary">{item.registeredKPI}</span>
                                                ) : (
                                                    <Form.Control
                                                        type="number"
                                                        step="1"
                                                        min="1"
                                                        value={item.registeredKPI}
                                                        onChange={(e) => handleUpdateKPI(item.annualJobId, e.target.value)}
                                                    />
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {isReadonly ? (
                                                    <span className="fw-bold text-info">{item.registeredPercent}%</span>
                                                ) : (
                                                    <Form.Control
                                                        type="number"
                                                        step="1"
                                                        min="0"
                                                        value={item.registeredPercent}
                                                        onChange={(e) => handleUpdatePercent(item.annualJobId, e.target.value)}
                                                    />
                                                )}
                                            </td>
                                            <td className="text-center fw-bold text-success">
                                                {score.toFixed(2)}
                                            </td>
                                            {!isReadonly && (
                                                <td className="text-center">
                                                    <Button variant="danger" size="sm" onClick={() => handleRemoveJob(item.annualJobId)}>
                                                        <i className="fas fa-trash"></i>
                                                    </Button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })
                            )}
                            {formData.details.length > 0 && (
                                <tr className="bg-light fw-bold">
                                    <td colSpan="8" className="text-end">Tổng điểm KPI dự kiến:</td>
                                    <td className="text-success fs-5">
                                        {totalConvertedScore.toFixed(2)}
                                    </td>
                                    {!isReadonly && <td></td>}
                                </tr>
                            )}
                        </tbody>
                        </Table>
                    </div>

                    {!isReadonly && (
                        <div className="d-flex justify-content-end mt-4">
                            <Button variant="primary" type="submit" disabled={loading}>
                                <i className="fas fa-save mr-2"></i>
                                {loading ? 'Đang lưu...' : (id === 'new' ? 'Lưu phiếu' : 'Cập nhật')}
                            </Button>
                        </div>
                    )}
                </Form>
            </Card.Body>
        </Card>
        </div>
    );
};

export default AnnualJobRegistrationForm;
